import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchRestaurantOnboardingOptions,
  onboardRestaurant,
} from '../utils/api';

const initialForm = {
  account_type: 'new',
  business_name: '',
  contact_person: '',
  email: '',
  phone_number: '',
  subdomain: '',
  service_country_id: '',
  state_of_origin: '',
  visibility_token: '',
  terms_accepted: false,
};

const makeSubdomain = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/https?:\/\//g, '')
    .replace(/\.trackash\.io/g, '')
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 30);

function RestaurantOnboarding() {
  const [form, setForm] = useState(initialForm);
  const [countries, setCountries] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchRestaurantOnboardingOptions()
      .then((response) => {
        setCountries(response?.data?.service_countries || []);
      })
      .catch(() => setCountries([]))
      .finally(() => setLoadingOptions(false));
  }, []);

  const trackashUrl = useMemo(
    () => `${form.subdomain || 'yourrestaurant'}.trackash.io`,
    [form.subdomain]
  );

  const change = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setForm((current) => ({
      ...current,
      [name]: name === 'subdomain' ? makeSubdomain(nextValue) : nextValue,
      ...(name === 'business_name' && !current.subdomain
        ? { subdomain: makeSubdomain(nextValue) }
        : {}),
    }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      const response = await onboardRestaurant(form);
      setResult(response?.data || {});
    } catch (error) {
      setErrors(error.response?.data?.errors || {});
      setErrors((current) => ({
        ...current,
        form: error.response?.data?.message || error.message || 'Registration failed.',
      }));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-[75vh] bg-gray-50 px-4 py-16">
        <div className="mx-auto max-w-2xl rounded-3xl border border-gray-100 bg-white p-8 text-center shadow-xl sm:p-12">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl text-green-700">✓</div>
          <h1 className="mt-6 font-playfair-display text-3xl font-bold text-secondary">Restaurant registered</h1>
          <p className="mt-3 font-montserrat leading-7 text-gray-600">
            {result.connection_required
              ? 'Your Trackash account and merchant profile are ready. Add your visibility token before your store is published.'
              : 'Your Trackash account is connected and your merchant profile is ready.'}
          </p>
          <a href={result.trackash_url} target="_blank" rel="noreferrer" className="mt-7 inline-flex rounded-full bg-primary px-7 py-3 font-montserrat font-semibold text-white">
            Open Trackash
          </a>
          <div><Link to="/" className="mt-5 inline-block font-montserrat text-sm font-semibold text-primary">Return to marketplace</Link></div>
        </div>
      </div>
    );
  }

  const fieldClass = 'mt-2 w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 font-montserrat outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20';

  return (
    <div className="bg-gray-50 px-4 py-10 sm:py-16">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <p className="font-montserrat text-sm font-bold uppercase tracking-[0.2em] text-primary">Restaurant partners</p>
          <h1 className="mt-3 font-playfair-display text-4xl font-bold text-secondary">Sell with us</h1>
          <p className="mx-auto mt-3 max-w-xl font-montserrat leading-7 text-gray-600">Join the marketplace and manage your menu, inventory and sales through Trackash.</p>
        </div>

        <form onSubmit={submit} className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl sm:p-10">
          <div className="mb-8 grid grid-cols-2 gap-3 rounded-2xl bg-gray-100 p-1.5">
            {[
              ['new', 'Create Trackash account'],
              ['existing', 'I already use Trackash'],
            ].map(([value, label]) => (
              <button key={value} type="button" onClick={() => setForm((current) => ({ ...current, account_type: value }))} className={`rounded-xl px-3 py-3 font-montserrat text-sm font-semibold transition ${form.account_type === value ? 'bg-white text-primary shadow-sm' : 'text-gray-500'}`}>
                {label}
              </button>
            ))}
          </div>

          {errors.form && <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 font-montserrat text-sm text-red-700">{errors.form}</div>}

          <div className="grid gap-5 sm:grid-cols-2">
            <label className="sm:col-span-2"><span className="font-montserrat text-sm font-semibold">Restaurant name</span><input name="business_name" value={form.business_name} onChange={change} required className={fieldClass} />{errors.business_name && <small className="text-red-600">{errors.business_name[0]}</small>}</label>
            <label><span className="font-montserrat text-sm font-semibold">Contact person</span><input name="contact_person" value={form.contact_person} onChange={change} className={fieldClass} /></label>
            <label><span className="font-montserrat text-sm font-semibold">Email</span><input type="email" name="email" value={form.email} onChange={change} required className={fieldClass} /></label>
            <label><span className="font-montserrat text-sm font-semibold">Phone number</span><input name="phone_number" value={form.phone_number} onChange={change} required className={fieldClass} placeholder="Include country code" /></label>
            <label><span className="font-montserrat text-sm font-semibold">Trackash subdomain</span><div className="relative"><input name="subdomain" value={form.subdomain} onChange={change} required className={`${fieldClass} pr-28`} /><span className="absolute right-4 top-1/2 mt-1 -translate-y-1/2 text-xs text-gray-400">.trackash.io</span></div><small className="text-gray-500">{trackashUrl}</small></label>

            {form.account_type === 'new' ? (
              <>
                <label><span className="font-montserrat text-sm font-semibold">Service country</span><select name="service_country_id" value={form.service_country_id} onChange={change} required className={fieldClass} disabled={loadingOptions}><option value="">{loadingOptions ? 'Loading...' : 'Select country'}</option>{countries.map((country) => <option key={country.id} value={country.id}>{country.country_name || country.name}</option>)}</select></label>
                <label><span className="font-montserrat text-sm font-semibold">City of operation</span><input name="state_of_origin" value={form.state_of_origin} onChange={change} required className={fieldClass} /></label>
              </>
            ) : (
              <label className="sm:col-span-2"><span className="font-montserrat text-sm font-semibold">Visibility token</span><input name="visibility_token" value={form.visibility_token} onChange={change} required className={fieldClass} /><small className="text-gray-500">Use the token supplied by your Trackash account.</small></label>
            )}
          </div>

          <label className="mt-7 flex items-start gap-3"><input type="checkbox" name="terms_accepted" checked={form.terms_accepted} onChange={change} required className="mt-1 h-5 w-5 rounded border-gray-300 text-primary" /><span className="font-montserrat text-sm leading-6 text-gray-600">I agree to the marketplace and Trackash onboarding terms.</span></label>

          <button disabled={submitting} className="mt-8 w-full rounded-full bg-primary px-6 py-4 font-montserrat font-bold text-white shadow-md transition hover:bg-primary/90 disabled:opacity-60">{submitting ? 'Setting up your restaurant...' : form.account_type === 'new' ? 'Create account and join' : 'Connect Trackash account'}</button>
        </form>
      </div>
    </div>
  );
}

export default RestaurantOnboarding;
